import { useState, useEffect } from 'react';
import { Box, Paper, Typography, CircularProgress, Chip, Stack, Alert } from '@mui/material';
import { API_ENDPOINTS } from '@/constants';

interface DbStatus {
    name: string;
    status: 'ok' | 'error';
    message?: string;
}

interface StatusResponse {
    timestamp: string;
    databases: DbStatus[];
    originalApiKey: DbStatus;
}

const StatusPage = () => {
    const [statusData, setStatusData] = useState<StatusResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchStatus = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(API_ENDPOINTS.STATUS, {
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch status: ${response.statusText}`);
            }

            const data: StatusResponse = await response.json();
            setStatusData(data);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchStatus();
    }, []);

    const getStatusColor = (status: DbStatus['status']) => {
        switch (status) {
            case 'ok':
                return 'success';
            default:
                return 'error';
        }
    };

    const getStatusLabel = (status: DbStatus['status']) => status.replace('_', ' ').toUpperCase();

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 150 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {error}
            </Alert>
        );
    }

    if (!statusData) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                No status data available
            </Alert>
        );
    }

    return (
        <Box sx={{ p: 2, maxWidth: '800px', margin: '0 auto' }}>
            <Typography variant="h2" component="h1" mb={1.5}>
                System status
            </Typography>
            <Paper sx={{ p: 1.5 }}>
                <Stack spacing={1}>
                    {statusData.databases.map((db, index) => (
                        <Box key={index}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box>
                                        <Typography variant="body2" component="h3" fontWeight={500}>
                                            {db.name}
                                        </Typography>
                                        {db.message && (
                                            <Typography variant="caption" color="text.secondary">
                                                {db.message}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                                <Chip label={getStatusLabel(db.status)} color={getStatusColor(db.status)} size="small" />
                            </Box>
                        </Box>
                    ))}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box>
                                <Typography variant="body2" component="h3" fontWeight={500}>
                                    {statusData.originalApiKey.name}
                                </Typography>
                                {statusData.originalApiKey.message && (
                                    <Typography variant="caption" color="text.secondary">
                                        {statusData.originalApiKey.message}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        <Chip
                            label={getStatusLabel(statusData.originalApiKey.status)}
                            color={getStatusColor(statusData.originalApiKey.status)}
                            size="small"
                        />
                    </Box>
                </Stack>
            </Paper>
        </Box>
    );
};

export default StatusPage;
